import { cmsApi as baseCmsApi } from "./api/cmsApiSlice";

// Types for CMS API responses
export interface SliderItem {
  id: number;
  title: string;
  description?: string;
  img?: {
    url: string;
    alternativeText?: string;
  };
}

export interface Slider {
  id: number;
  documentId: string;
  title: string;
  subTitle?: string;
  sliders: SliderItem[];
  createdAt: string;
  updatedAt: string;
}

export interface StrapiTextNode {
  type: "text";
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
}

export interface StrapiBlock {
  type: string;
  children?: Array<StrapiTextNode | StrapiBlock>;
  format?: "ordered" | "unordered";
  url?: string;
  level?: number;
  image?: { url?: string; alternativeText?: string };
  [key: string]: unknown;
}

export type StrapiBlocks = StrapiBlock[];

export interface NoticeItem {
  id?: number;
  title: string;
  description?: string | StrapiBlocks;
  excerpt?: string | StrapiBlocks;
  img?: {
    url: string;
    alternativeText?: string;
  };
  date?: string;
  file?: {
    url: string;
  };
}

export interface Notice {
  id: number;
  documentId: string;
  title: string;
  subTitle?: string;
  notices: NoticeItem[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Contact {
  id: number;
  documentId: string;
  title?: string;
  address: string;
  emailAddress?: string;
  phoneNumber?: string;
  websiteUrl?: string;
  // Map related fields
  latitude?: number;
  longitude?: number;
  placeName?: string;
  zoomLevel?: number;
  // Fallback fields for backward compatibility
  phone?: string;
  email?: string;
  webUrl?: string;
  officeHours?: string;
  socialLinks?: {
    facebook?: string;
    linkedin?: string;
    twitter?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Footer {
  id: number;
  documentId: string;
  poweredByTitle: string;
  poweredByLogo?: {
    url: string;
    alternativeText?: string;
  };
  developedByTitle: string;
  developedByLogo?: {
    url: string;
    alternativeText?: string;
  };
  bottomText: string;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

// The Club section interfaces
export interface ChairpersonMessage {
  id: number;
  documentId: string;
  name: string;
  title: string;
  classYear?: string;
  profileImage?: {
    url: string;
    alternativeText?: string;
  };
  greeting?: string;
  message: string;
  closing?: string;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface BoardMember {
  id?: number;
  name: string;
  position: string;
  classYear: string;
  profileImage?: {
    url: string;
    alternativeText?: string;
  };
  bio?: string;
}

export interface BoardOfDirectors {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  boardMembers: BoardMember[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface FormerMember {
  id?: number;
  name: string;
  tenure: string;
  position: string;
  image?: CMSImage;
}

export interface FormerBods {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  additionalInfo?: string;
  formerMembers: FormerMember[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommitteeMember {
  id: number;
  name: string;
  clubId: string;
  position: string;
  email: string;
  phone: string;
}

export interface Committee {
  id?: number;
  name: string;
  chairperson: string;
  memberCount: number;
  description?: string;
  members?: CommitteeMember[];
}

export interface SubCommittees {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  committees: Committee[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface LawSection {
  id?: number;
  sectionNumber: string;
  title: string;
  content: string;
}

export interface MoaLaws {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  sections: LawSection[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  moa?: { url: string };
  laws?: { url: string };
}

export interface Club {
  id?: number;
  name: string;
  location: string;
  memberCount: number;
  description?: string;
  contactInfo?: string;
  link: string;
  logo?: {
    id: number;
    documentId: string;
    name: string;
    alternativeText?: string;
    caption?: string;
    width: number;
    height: number;
    formats?: {
      thumbnail?: {
        ext: string;
        url: string;
        hash: string;
        mime: string;
        name: string;
        path?: string;
        size: number;
        width: number;
        height: number;
        sizeInBytes: number;
      };
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: string;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
}

export interface AffiliatedClubs {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  clubs: Club[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Objective {
  id?: number;
  title: string;
  description: string;
}

export interface AlumniAssociation {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  introduction: string;
  objectives: Objective[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

// CSR & Welfare section interfaces
export interface MainHeader {
  title: string;
  subtitle: string;
  description: string;
}

export interface InitiativeItem {
  title: string;
  description: string;
  highlight: string;
  icon?: string;
  isActive?: boolean;
  order?: number;
}

export interface KeyInitiatives {
  sectionTitle: string;
  sectionSubtitle: string;
  description: string;
  initiatives: InitiativeItem[];
}

export interface BenefitItem {
  title: string;
  description: string;
  buttonText: string;
  icon?: string;
  isActive?: boolean;
  order?: number;
}

export interface AlumniInfo {
  name: string;
  batch: string;
  position: string;
  initials?: string;
}

export interface AlumniTestimonial {
  title: string;
  quote: string;
  alumni: AlumniInfo;
}

export interface MemberBenefits {
  sectionTitle: string;
  sectionSubtitle: string;
  description: string;
  benefits: BenefitItem[];
  testimonial: AlumniTestimonial;
}

export interface CTAButton {
  label: string;
  link: string;
  isExternal?: boolean;
  buttonStyle?: string;
  isActive?: boolean;
  order?: number;
}

export interface FooterCTA {
  title: string;
  description: string;
  buttons: CTAButton[];
}

export interface CSRWelfare {
  id: number;
  documentId: string;
  mainHeader: MainHeader;
  keyInitiatives: KeyInitiatives;
  memberBenefits: MemberBenefits;
  footerCTA: FooterCTA;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface FaqItem {
  id: number;
  title: string;
  description: string;
}

export interface Faq {
  id: number;
  documentId: string;
  title: string;
  faqItems: FaqItem[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

// Add Services interface
export interface ServiceItem {
  id?: number;
  title: string;
  description?: string;
  img?: {
    url: string;
    alternativeText?: string;
  };
}

export interface Services {
  id: number;
  documentId: string;
  title: string;
  description?: string;
  services: ServiceItem[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PoweredBy {
  id: number;
  title: string;
  img: {
    id: number;
    documentId: string;
    name: string;
    alternativeText?: string;
    caption?: string;
    width: number;
    height: number;
    formats?: any;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: string;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CMSImage {
  id: number;
  documentId: string;
  name: string;
  alternativeText?: string;
  caption?: string;
  width: number;
  height: number;
  formats?: any;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  provider_metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface MemberStat {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  title1: string;
  title2: string;
  title3: string;
  poweredBy: PoweredBy;
}

export interface GalleryItem {
  id: number;
  title?: string;
  img: CMSImage[];
}

export interface Gallery {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  title: string;
  galleryItem: GalleryItem[];
}

export interface AboutIba {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  title: string;
  galleryItem: GalleryItem[];
  address: string;
  phone1: number;
  phone2: number;
}
export interface PublicationItem {
  id: number;
  title?: string;
  description?: string;
  img: CMSImage;
}

export interface PublicationAndMedia {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  title: string;
  publicationItem: PublicationItem[];
}

export interface CMSResponse<T> {
  data: T[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface CMSSingleResponse<T> {
  data: T;

  meta: {};
}

// CMS API endpoints
export const cmsApi = baseCmsApi.injectEndpoints({
  endpoints: (builder) => ({
    // Sliders for AlumniSpotlight
    getSliders: builder.query<CMSSingleResponse<Slider>, void>({
      query: () => "slider?populate[sliders][populate]=img",
      providesTags: ["sliders"],
    }),

    // Notices for StoriesSection (previously Stories)
    getNotices: builder.query<CMSSingleResponse<Notice>, void>({
      query: () => "notic?populate[notices][populate]=*",
      providesTags: ["notices"],
    }),

    // Contact information for ContactSection
    getContact: builder.query<CMSSingleResponse<Contact>, void>({
      query: () => "contact?populate=*",
      providesTags: ["contact"],
    }),
    getServices: builder.query<any, void>({
      query: () =>
        "service?populate[serviceCategories][populate]=image&populate[heroSection]=*&populate[statisticsSection][populate]=statistics&populate[seo]=*",
    }),
    // Get individual service by ID with full population
    getServiceById: builder.query<any, string>({
      query: (id) => `service/${id}`,
    }),
    // Footer information
    // getFooter: builder.query<CMSSingleResponse<Footer>, void>({
    //   query: () =>
    //     "footer?populate[0]=poweredByLogo&populate[1]=developedByLogo",
    //   providesTags: ["footer"],
    // }),

    // Get single notice by ID - keeping for backward compatibility
    getNoticeById: builder.query<CMSSingleResponse<NoticeItem>, string>({
      query: (_id) => `notic?populate[notices][populate]=img`,
      providesTags: (_result, _error, id) => [{ type: "notices", id }],
    }), // The Club section APIs
    getChairpersonMessage: builder.query<
      CMSSingleResponse<ChairpersonMessage>,
      void
    >({
      query: () => "chairperson-message?populate=*",
      providesTags: ["chairperson-message"],
    }),
    getBoardOfDirectors: builder.query<
      CMSSingleResponse<BoardOfDirectors>,
      void
    >({
      query: () =>
        "board-of-directors?populate[boardMembers][populate]=profileImage",
      providesTags: ["board-of-directors"],
    }),

    getFormerBods: builder.query<CMSSingleResponse<FormerBods>, void>({
      query: () => "former-bods?populate[formerMembers][populate]=image",
      providesTags: ["former-bods"],
    }),

    getSubCommittees: builder.query<CMSSingleResponse<SubCommittees>, void>({
      query: () => "sub-committees?populate[committees][populate][members]=*",
      providesTags: ["sub-committees"],
    }),

    getMoaLaws: builder.query<CMSSingleResponse<MoaLaws>, void>({
      query: () => "moa-laws?populate=*",
      providesTags: ["moa-laws"],
    }),

    getAffiliatedClubs: builder.query<CMSSingleResponse<AffiliatedClubs>, void>(
      {
        query: () => "affiliated-clubs?populate[clubs][populate]=logo",
        providesTags: ["affiliated-clubs"],
      },
    ),

    getAlumniAssociation: builder.query<
      CMSSingleResponse<AlumniAssociation>,
      void
    >({
      query: () => "alumni-association?populate=*",
      providesTags: ["alumni-association"],
    }),

    // CSR & Welfare section API
    getCSRWelfare: builder.query<CMSSingleResponse<CSRWelfare>, void>({
      query: () =>
        "csr-welfare?populate[mainHeader]=*&populate[keyInitiatives][populate][initiatives][populate]=images&populate[memberBenefits][populate][benefits]=*&populate[memberBenefits][populate][testimonial][populate][alumni]=*&populate[footerCTA][populate][buttons]=*",
      providesTags: ["csr-welfare"],
    }),
    getFaq: builder.query<CMSSingleResponse<Faq>, void>({
      query: () => "faq?populate[faqItems]=*",
      providesTags: ["faq"],
    }),
    getMemberStat: builder.query<CMSSingleResponse<MemberStat>, void>({
      query: () => "/member-stat?populate[poweredBy][populate]=img",
    }),
    getGallery: builder.query<CMSSingleResponse<Gallery>, void>({
      query: () => "/gallery?populate[galleryItem][populate]=img",
    }),
    getAboutIba: builder.query<CMSSingleResponse<AboutIba>, void>({
      query: () => "/about-iba",
    }),
    getPublicationAndMedia: builder.query<
      CMSSingleResponse<PublicationAndMedia>,
      void
    >({
      query: () =>
        "/publication-and-media?populate[publicationItem][populate]=img",
    }),
    getAboutUs: builder.query<any, void>({
      query: () => "/about-us?populate=video",
    }),
    getPowerfulAlumniNetwork: builder.query<any, void>({
      query: () => "/powerful-alumni-network?populate=*",
    }),
    getPrivacyPolicy: builder.query<any, void>({
      query: () => "/privacy-policy",
    }),
    getCookiePolicy: builder.query<any, void>({
      query: () => "/cookie-policy",
    }),
    getLegal: builder.query<any, void>({
      query: () => "/legal",
    }),
    getHowToMakePayment: builder.query<any, void>({
      query: () =>
        "/how-to-make-payment?publicationState=live&populate[videoItems][populate][video]=true",
    }),
    getNewsMediaPublication: builder.query<any, void>({
      query: () =>
        "/news-media-publication?populate[news][populate]=thumbnail&populate[publication][populate][0]=thumbnail&populate[publication][populate][1]=file&populate[media][populate]=*",
    }),
    getCopyRightAndPoweredBy: builder.query<any, void>({
      query: () =>
        "/copyright-and-powered-by?populate[poweredBy][populate]=logo",
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetSlidersQuery,
  useGetNoticesQuery,
  useGetContactQuery,
  // useGetFooterQuery,
  useGetNoticeByIdQuery,
  useGetChairpersonMessageQuery,
  useGetBoardOfDirectorsQuery,
  useGetFormerBodsQuery,
  useGetSubCommitteesQuery,
  useGetMoaLawsQuery,
  useGetAffiliatedClubsQuery,
  useGetAlumniAssociationQuery,
  useGetCSRWelfareQuery,
  useGetFaqQuery,
  useGetMemberStatQuery,
  useGetGalleryQuery,
  useGetPublicationAndMediaQuery,
  useGetServicesQuery,
  useGetServiceByIdQuery,
  useGetAboutIbaQuery,
  useGetAboutUsQuery,
  useGetPowerfulAlumniNetworkQuery,
  useGetPrivacyPolicyQuery,
  useGetCookiePolicyQuery,
  useGetLegalQuery,
  useGetHowToMakePaymentQuery,
  useGetNewsMediaPublicationQuery,
  useGetCopyRightAndPoweredByQuery,
} = cmsApi;
